<template>
  <b-popover
    target="twilio-status-btn"
    triggers="hover focus"
    placement="bottomleft"
    custom-class="twilio-status-popover"
    boundary="viewport"
    no-fade
  >
    <template #title>
      <div class="d-flex align-items-center">
        <feather-icon icon="ActivityIcon" size="14" class="mr-50 text-primary" />
        <span class="font-weight-bolder text-dark" style="font-size: 0.9rem; letter-spacing: -0.01em;">
          Statut Services
        </span>
        <span class="ml-auto badge badge-light-primary text-uppercase" style="font-size: 0.6rem; font-weight: 700;">
          Twilio
        </span>
      </div>
    </template>

    <!-- Error state -->
    <div v-if="overallStatus === 'error'" class="twilio-status-message text-center py-1">
      <feather-icon icon="AlertTriangleIcon" size="24" class="text-warning mb-50" />
      <p class="mb-0 text-dark font-weight-bold" style="font-size: 0.85rem;">
        Indisponible
      </p>
      <small class="text-muted">Échec de connexion au status</small>
    </div>

    <!-- Loading state -->
    <div v-else-if="overallStatus === 'loading'" class="twilio-status-message text-center py-1">
      <b-spinner small variant="primary" class="mb-50" />
      <p class="mb-0 text-muted" style="font-size: 0.8rem;">
        Vérification...
      </p>
    </div>

    <!-- Services list -->
    <template v-else>
      <div class="services-container px-50 py-25">
        <div
          v-for="service in services"
          :key="service.id"
          class="twilio-service-row d-flex align-items-center justify-content-between py-50 px-75 mb-25 rounded"
        >
          <div class="d-flex align-items-center pr-1" style="min-width: 0; flex: 1;">
            <div class="status-indicator mr-75 position-relative flex-shrink-0" style="width: 8px; height: 8px;">
              <span class="twilio-dot" :style="{ backgroundColor: statusColor(service.status) }" />
              <span
                v-if="service.status !== 'operational' && service.status !== 'loading'"
                class="twilio-dot-pulse"
                :style="{ backgroundColor: statusColor(service.status) }"
              />
            </div>
            <span class="service-name text-dark font-weight-medium" style="font-size: 0.85rem; white-space: nowrap;">
              {{ service.label }}
            </span>
          </div>
          <div class="flex-shrink-0 ml-75">
            <b-badge pill :variant="badgeVariant(service.status)" style="font-size: 0.65rem; padding: 0.4em 0.8em; white-space: nowrap;">
              {{ statusLabel(service.status) }}
            </b-badge>
          </div>
        </div>
      </div>

      <!-- Active incidents -->
      <div
        v-if="activeIncidents.length"
        class="twilio-incidents mt-1 px-25 pt-1"
        style="border-top: 1px dashed rgba(0,0,0,0.1);"
      >
        <div class="text-uppercase text-muted font-weight-bolder mb-50" style="font-size: 0.65rem; letter-spacing: 0.05em;">
          Incidents en cours
        </div>
        <div
          v-for="incident in activeIncidents"
          :key="incident.id"
          class="twilio-incident-row d-flex align-items-start mb-50"
        >
          <feather-icon
            icon="InfoIcon"
            size="12"
            class="mt-25 mr-50 flex-shrink-0"
            :style="{ color: impactColor(incident.impact) }"
          />
          <span
            class="text-dark"
            style="font-size: 0.75rem; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;"
          >
            {{ incident.name }}
          </span>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="twilio-status-footer d-flex align-items-center justify-content-between mt-1 pt-75"
        style="border-top: 1px solid rgba(0,0,0,0.05);"
      >
        <div class="d-flex align-items-center text-muted" style="font-size: 0.7rem;">
          <feather-icon icon="ClockIcon" size="10" class="mr-25" />
          <span>Mise à jour {{ lastUpdatedLabel }}</span>
        </div>
        <b-link
          href="https://status.twilio.com"
          target="_blank"
          rel="noopener noreferrer"
          class="font-weight-bold d-flex align-items-center"
          style="font-size: 0.75rem; text-decoration: none;"
        >
          <span>Live Status</span>
          <feather-icon icon="ExternalLinkIcon" size="10" class="ml-25" />
        </b-link>
      </div>
    </template>
  </b-popover>
</template>

<script>
import {
  BPopover,
  BSpinner,
  BBadge,
  BLink,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import {
  statusColor,
  statusLabel,
  badgeVariant,
  impactColor,
} from './twilioStatusConfig'

export default {
  name: 'TwilioStatusPopover',

  components: {
    BPopover,
    BSpinner,
    BBadge,
    BLink,
    FeatherIcon,
  },

  props: {
    overallStatus: {
      type: String,
      required: true,
    },
    services: {
      type: Array,
      required: true,
    },
    activeIncidents: {
      type: Array,
      default: () => [],
    },
    lastUpdatedLabel: {
      type: String,
      required: true,
    },
  },

  methods: {
    statusColor,
    statusLabel,
    badgeVariant,
    impactColor,
  },
}
</script>

<style scoped>
.twilio-dot {
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  transition: background-color 0.4s ease;
  z-index: 2;
}

.twilio-dot-pulse {
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  z-index: 1;
  animation: twilio-pulse-expand 2s infinite ease-in-out;
}

@keyframes twilio-pulse-expand {
  0%   { transform: scale(1); opacity: 0.8; }
  100% { transform: scale(2.5); opacity: 0; }
}

.twilio-service-row {
  transition: background 0.15s ease;
  margin: 0 4px;
}

.twilio-service-row:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.twilio-service-row + .twilio-service-row {
  border-top: 1px solid rgba(0, 0, 0, 0.04);
}

.twilio-incident-row {
  padding: 4px 0;
}

.twilio-incident-row:last-child {
  margin-bottom: 0 !important;
}

.twilio-status-message {
  padding: 1rem 0.5rem;
}
</style>

<style>
/* ─── Portal-rendered popover, must be global ───────────────────────────── */
.twilio-status-popover {
  width: auto !important;
  min-width: 300px !important;
  max-width: 400px !important;
}

.twilio-status-popover.popover {
  border: 1px solid #16B5EA !important;
  border-radius: 0.6rem !important;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1) !important;
  padding: 0 !important;
  overflow: hidden;
}

.twilio-status-popover .popover-header {
  background-color: transparent !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important;
  border-top: none !important;
  border-left: none !important;
  border-right: none !important;
  padding: 0.8rem 1rem !important;
}

/* Dark mode overrides */
.dark-layout .twilio-status-popover.popover {
  background-color: #283046 !important;
  border-color: #16B5EA !important;
}

.dark-layout .twilio-status-popover .popover-header {
  border-bottom-color: rgba(255, 255, 255, 0.05) !important;
}

.dark-layout .twilio-status-popover .popover-header span,
.dark-layout .twilio-status-popover .popover-header .feather {
  color: #b4b7bd !important;
}

.dark-layout .twilio-status-popover .popover-body {
  background-color: #283046 !important;
  border-top: none !important;
}

.dark-layout .twilio-status-popover .arrow::after {
  border-bottom-color: #283046 !important;
}

.twilio-status-popover .popover-body {
  padding: 0.8rem 1rem !important;
  color: #6e6b7b !important;
}

.twilio-status-popover .arrow::before,
.twilio-status-popover .arrow::after {
  border-bottom-color: #16B5EA !important;
}
</style>
